import { MutationCache, QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import type { ReactNode } from 'react'
import { toast } from 'sonner'
import { getErrorMessage } from '@/lib/utils'
import { queryPersistOptions } from '@/lib/offline/queryPersist'

// #286 — app-wide safety net so no mutation fails invisibly: any mutation that does
// not surface its own error gets a toast with the server message. A mutation opts out
// by either declaring a config-level onError (visible here as mutation.options.onError)
// or setting meta.skipGlobalErrorToast — the latter covers call sites that handle the
// error through an inline .mutate(vars, { onError }) callback, which the cache cannot see.
export function createAppMutationCache(): MutationCache {
  return new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.onError) return
      if (mutation.meta?.skipGlobalErrorToast) return
      toast.error(getErrorMessage(error))
    },
  })
}

export const queryClient = new QueryClient({
  mutationCache: createAppMutationCache(),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

// PersistQueryClientProvider rather than a bare QueryClientProvider plus a fire-and-forget
// restore: it holds queries back until the persisted cache has been read, so a cold offline
// start cannot have the add-expense lookups mount, fetch, and fail in the window before
// their cached data lands — which would defeat the offline form this exists for.
export function QueryProvider({ children }: { children: ReactNode }) {
  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={queryPersistOptions}>
      {children}
    </PersistQueryClientProvider>
  )
}
